import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { showModal, installModalFallback } from "./modalHelper";
import TacheFormModal, { TACHE_MODAL_ID } from "./TacheFormModal";
import TacheRow, { TacheTableHead } from "./TacheRow";
import useReferentiels from "./useReferentiels";
import { API, STATUTS_TACHE, findStatutTache, unwrap, apiMessage, toast } from "./projetsCommon";
import "./GestionProjets.css";

/** Vue globale des tâches de tous les projets, filtrable par employé (« tâches de Karim »). */
const TachesPlanning = () => {
    const { employes } = useReferentiels();
    const [taches, setTaches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [employeId, setEmployeId] = useState("");
    const [statut, setStatut] = useState("");
    const [ouvertes, setOuvertes] = useState(true);
    const [retardOnly, setRetardOnly] = useState(false);
    const [search, setSearch] = useState("");

    // Modification : il faut les phases du projet de la tâche
    const [editing, setEditing] = useState(null);
    const [phases, setPhases] = useState([]);
    const [openKey, setOpenKey] = useState(0);

    useEffect(() => installModalFallback(), []);
    useEffect(() => { load(); }, [employeId, statut, ouvertes]); // eslint-disable-line react-hooks/exhaustive-deps

    const load = () => {
        const params = new URLSearchParams();
        if (employeId) params.append("employeId", employeId);
        if (statut) params.append("statut", statut);
        params.append("ouvertes", ouvertes);
        setLoading(true);
        axiosInstance.get(`${API.tachesGlobal}?${params}`)
            .then((res) => setTaches(unwrap(res) || []))
            .catch((error) => toast("error", apiMessage(error, "Impossible de charger les tâches")))
            .finally(() => setLoading(false));
    };

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return taches.filter((t) => {
            if (retardOnly && !t.enRetard) return false;
            if (!term) return true;
            return [t.titre, t.projetNom, t.projetCode, t.phaseNom, t.assigneNom].filter(Boolean).join(" ").toLowerCase().includes(term);
        });
    }, [taches, search, retardOnly]);

    const compteurs = useMemo(() => {
        const c = {};
        taches.forEach((t) => { const v = findStatutTache(t.statut).value; c[v] = (c[v] || 0) + 1; });
        return c;
    }, [taches]);

    const changeStatut = async (t, s) => {
        try { await axiosInstance.put(API.tacheSuivi(t.projetId, t.id), { statut: s }); load(); }
        catch (error) { toast("error", apiMessage(error, "Mise à jour échouée")); }
    };

    const openEdit = async (t) => {
        try {
            const res = await axiosInstance.get(API.planning(t.projetId));
            setPhases(unwrap(res)?.phases || []);
        } catch { setPhases([]); }
        setEditing(t);
        setOpenKey((k) => k + 1);
        showModal(TACHE_MODAL_ID);
    };

    return (
        <div className="container-fluid mt-4">
            <h3 className="my-3">Tâches</h3>

            <div className="d-flex flex-wrap mb-3">
                {STATUTS_TACHE.map((s) => (
                    <span key={s.value} className={`badge ${s.css} mr-2 mb-1 p-2`}>
                        <i className={`fas ${s.icon} mr-1`}></i>{s.label} : {compteurs[s.value] || 0}
                    </span>
                ))}
            </div>

            <div className="form-row align-items-end mb-3">
                <div className="col-md-3 mb-2">
                    <label className="small mb-1">Employé</label>
                    <select className="form-control" value={employeId} onChange={(e) => setEmployeId(e.target.value)}>
                        <option value="">Tous les employés</option>
                        {employes.map((e) => <option key={e.id} value={e.id}>{e.nomComplet}</option>)}
                    </select>
                </div>
                <div className="col-md-2 mb-2">
                    <label className="small mb-1">Statut</label>
                    <select className="form-control" value={statut} onChange={(e) => setStatut(e.target.value)}>
                        <option value="">Tous</option>
                        {STATUTS_TACHE.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                </div>
                <div className="col-md-3 mb-2">
                    <label className="small mb-1">Recherche</label>
                    <input className="form-control" placeholder="Tâche, projet, phase…" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <div className="col-md-4 mb-2 d-flex flex-wrap align-items-center">
                    <div className="custom-control custom-switch mr-3">
                        <input type="checkbox" className="custom-control-input" id="ouvertes" checked={ouvertes} onChange={(e) => setOuvertes(e.target.checked)} />
                        <label className="custom-control-label" htmlFor="ouvertes">Non terminées</label>
                    </div>
                    <div className="custom-control custom-switch mr-3">
                        <input type="checkbox" className="custom-control-input" id="retard" checked={retardOnly} onChange={(e) => setRetardOnly(e.target.checked)} />
                        <label className="custom-control-label" htmlFor="retard">En retard</label>
                    </div>
                    <span className="small text-muted ml-auto">{filtered.length} tâche(s)</span>
                </div>
            </div>

            <div className="table-responsive">
                <table className="table table-sm table-hover bg-white">
                    <TacheTableHead showProjet />
                    <tbody>
                        {loading ? <tr><td colSpan="9" className="text-center py-4">Chargement...</td></tr>
                            : filtered.length === 0 ? <tr><td colSpan="9" className="text-center text-muted py-4">Aucune tâche</td></tr>
                                : filtered.map((t) => <TacheRow key={t.id} t={t} showProjet onStatut={changeStatut} onEdit={openEdit} />)}
                    </tbody>
                </table>
            </div>

            {editing && (
                <TacheFormModal projetId={editing.projetId} tache={editing} openKey={openKey}
                    phases={phases} employes={employes} onSaved={load} />
            )}
        </div>
    );
};

export default TachesPlanning;
